import React, {useMemo, useRef, useState} from 'react';
import linkifyHtml from 'linkify-html';
import {ContentState, EditorState, getDefaultKeyBinding,KeyBindingUtil} from "draft-js"
import {convertToHTML} from "draft-convert"
import createTextAlignmentPlugin from '@draft-js-plugins/text-alignment';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import createLinkPlugin from '@draft-js-plugins/anchor';
import createCounterPlugin from '@draft-js-plugins/counter';
import createToolbarPlugin, {Separator} from '@draft-js-plugins/static-toolbar';
import htmlToDraft from 'html-to-draftjs';
import Editor from '@draft-js-plugins/editor';
import ScrollArea from "react-scrollbar";
import punycode from "punycode";
const {hasCommandModifier} = KeyBindingUtil;

import {
    BoldButton,
    ItalicButton,
    OrderedListButton,
    UnderlineButton,
    UnorderedListButton
} from '@draft-js-plugins/buttons';

// const BoldButton = createInlineStyleButton({style: "BOLD", children: [
//         (
//             <div className="flex-center-column">
//                 <i className="fas fa-bold"/>
//             </div>
//         )
//     ]
// })
import linkStyles from './linkStyles.css';

const exporterConfig = {
    styleToHTML: (style) => {
        if (style === "center") {
            return <span style={{textAlign:"center"}}/>
        }

        if (style === "left") {
            return <span style={{textAlign:"left"}}/>
        }

        if (style === "right") {
            return <span style={{textAlign:"right"}}/>
        }
    },
    blockToHTML: (block) => {
        // if (block.type === 'LINK') {
        // console.log('blockToHTML', block.type)

        // if (extractLinks(block.text)?.length === 1 && !TB.includes(block.text, "</a>")) {
        //     return <a href={block.text} target="_blank" />
        // }

        if (block.type === 'PARAGRAPH') {
            return <p />;
        }
    },
    entityToHTML: (entity, originalText) => {
        if (entity.type === 'LINK') {
            return <a href={entity.data.url}>{originalText}</a>;
        }
        return originalText;
    }
}

const toHTML = (content) => convertToHTML(exporterConfig)(content);

const ScrollWrapper = (props) => {
    return props.enableScroll ? (
        <ScrollArea
            speed={0.8}
            className="area"
            smoothScrolling={true}
            contentClassName="scroll-area"
            stopScrollPropagation={true}
            horizontal={false}>
            {props.children}
        </ScrollArea>
    ) : props.children
};


const checkLength = (content) => {

    let decodeUnicode = (str) => {
        return punycode.ucs2.decode(str);
    }; // func to handle unicode characters


    let plainText = content.getPlainText('');
    let regex = /(?:\r\n|\r|\n)/g; // new line, carriage return, line feed

    let cleanString = plainText.replace(regex, '').trim();
    return decodeUnicode(cleanString).length
}


const ChatEditor = (props) => {

    const editorRef = useRef(null);

    const [htmlMessage, setHtmlMessage] = useState('');
    const [messageLength, setMessageLength] = useState(0);


    const [editorState, setEditorState] = useState(() => {
        if (!TB.isEmpty(props.value)) {
            let cleanBreaks = TB.replace(props.value,/\n/g, "<p></p>");


            const blocksFromHTML = htmlToDraft(cleanBreaks);

            return EditorState.createWithContent(
                ContentState.createFromBlockArray(
                    blocksFromHTML.contentBlocks,
                    blocksFromHTML.entityMap,
                )
            )
        }else return EditorState.createEmpty()
    });

    const onChange = (content) => {
        let currentContent = content.getCurrentContent();
        let contentLength = checkLength(currentContent);

        // by checking for missing previous message length and missing new message length,
        // it must be a new line, therefore we will not save if the onHandleReturn prop is set
        if(props.onHandleReturn && !messageLength && !contentLength) {
            return;
        }
        if (currentContent) {

            setEditorState(content)
            const options = { defaultProtocol: 'https' };
            const html = linkifyHtml(toHTML(content.getCurrentContent()), options);

            setHtmlMessage(html)
            let contentLength = checkLength(currentContent);

            setMessageLength(contentLength)
            if (contentLength) props.onChange(html, contentLength);
        }
    }


    const [plugins, toolbarBtns, Toolbar, CharCounter] = useMemo(() => {
        const toolbarPlugin = createToolbarPlugin();
        const linkifyPlugin = createLinkifyPlugin({target: '_blank'});
        const textAlignmentPlugin = createTextAlignmentPlugin();
        const counterPlugin = createCounterPlugin({
            theme: {
                counter: 'editor-counter-limit',
                counterOverLimit: 'editor-counter-over',
            }
        });
        const linkPlugin = createLinkPlugin({
            theme: linkStyles,
            placeholder: 'https://…',
        });
        return [
            [...(props.plugins || [linkPlugin]),
                toolbarPlugin,
                textAlignmentPlugin,
                linkifyPlugin,
                counterPlugin
            ],
            props.toolbarBtns || [
                BoldButton,
                ItalicButton,
                UnderlineButton,
                Separator,
                UnorderedListButton,
                OrderedListButton,
                linkPlugin.LinkButton
            ],
            toolbarPlugin.Toolbar,
            counterPlugin.CharCounter,
        ]
    }, []);

    const reset = async() => {
        await setEditorState(EditorState.createEmpty());
        await setHtmlMessage('')
        await setMessageLength(0)
        await TB.wait(250)
        return editorRef?.current?.focus();
    }
    const handleReturn = (e) => {
        if(e) e.preventDefault();
        if(messageLength) {
            return props.onHandleReturn(htmlMessage, messageLength, reset)
        }
        return true;
    }
    const keyBindings = event => {
        if ((event.keyCode === 10 || event.keyCode === 13) && hasCommandModifier(event)) {
            return 'editor-submit';
        }
        return getDefaultKeyBinding(event);
    }

    const handleKeyCommand = (command) => {
        if (command === 'editor-submit') {
            // Perform a request to save your contents, set
            // a new `editorState`, etc.
            if (props.onHandleReturn) {
                handleReturn()
            }
            return 'handled';
        }
        return 'not-handled';
    }

    return (
        <div className={"editor " + (props.enableScroll ? 'editor-scrollable' : '')}>

            {!props.hideToolbar && (
                <Toolbar>
                    {
                        // may be use React.Fragment instead of div to improve performance after React 16
                        (externalProps) => (
                            <React.Fragment>
                                {toolbarBtns.map((ToolbarBtn, i) => {
                                    return (
                                        <ToolbarBtn
                                            key={`toolbar-btn${i}`}
                                            {...externalProps}
                                            htmlMessage={htmlMessage}
                                            disabled={props.disabled}
                                            messageLength={messageLength}
                                            reset={reset}
                                        />
                                    )
                                })}

                                {/*<textAlignmentPlugin.TextAlignment {...externalProps}/>*/}
                                {/*<Separator {...externalProps} />*/}
                                {/*<HeadlinesButton {...externalProps} />*/}
                                {/*<BlockquoteButton {...externalProps} />*/}

                            </React.Fragment>
                        )
                    }
                </Toolbar>
            )}

            <ScrollWrapper enableScroll={props.enableScroll}>
                <Editor
                    ref={editorRef}
                    readOnly={props.disabled || props.isReadOnly}
                    placeholder={props.placeholder}
                    handleKeyCommand={handleKeyCommand}
                    keyBindingFn={keyBindings}
                    editorState={editorState}
                    onChange={onChange}
                    plugins={plugins}
                    // handleReturn={props.onHandleReturn ? handleReturn : undefined}
                />
            </ScrollWrapper>

            {props.maxLength && !props.hideCounter && (
                <div className="editor-counter">
                    <CharCounter limit={props.maxLength}/>
                    <span>/{props.maxLength}</span>
                </div>
            )}

        </div>
    )
}

export default ChatEditor;
class ToolbarButton extends React.Component {
    render() {
        return (
            <div className={"bi09khh " + (this.props.className)}>
                <button onClick={this.props.onClick} disabled={this.props.disabled} className="bc4rxid">
                    {this.props.children}
                </button>
            </div>
        );
    }
}

export {
    ToolbarButton,
    toHTML
}